import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import appServices from "../services/app-services/appServices";
import DropDown from "../components/DropDown";
import LoadingModal from "../components/LoadingModal";
import PostCard from "../components/lists/PostCard";
import MebukuIdUpgradeBanner from "../components/MebukuIdUpgradeBanner";
import LoadMoreButton from "../components/LoadMoreButton";
import { ErrorMessageModal } from "../components/ErrorMessageModal";
import "../App.css";
import { Button, SelectChangeEvent } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { styles } from "./styles";

type SortType = "created" | "commnet"
type OrderType = "asc" | "desc"

interface PostParams {
  page: number,
  sort_type: SortType,
  order: OrderType,
  vote_open: boolean | null
}

export default function FacilitatorList() {
  const sortOptions = [
    { value: "created_desc", name: T("sort_by_new") },
    { value: "created_asc", name: T("sort_by_old") },
    { value: "comment_desc", name: T("sort_by_most_comments") },
    { value: "comment_asc", name: T("sort_by_least_comments") },
  ]

  const voteOpenOptions = [
    { value: true, name: T("vote_in_session") },
    { value: false, name: T("vote_expired") },
  ]

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [posts, setPosts] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});
  const [selectVoteOpenOption, setSelectVoteOpenOption] = useState<boolean | null>(null);
  const [selectSortOption, setSelectSortOption] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string[]>([]);

  const getOpinionPosts = async (params: PostParams) => {
    try {
      setIsLoading(true);
      const res = await appServices.getOpinionPosts(params);
      if (params.page === 1) {
        setPosts(res.posts)
      } else {
        setPosts([...posts, ...res.posts])
      }
      setPagination(res.pagination)
    } catch (error: any) {
      setErrMsg([error?.response?.data?.message ?? T("api_unknown_error")])
      setShowErrorModal(true)
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const sortOrder = parseSortOrder()

    getOpinionPosts({
      page: 1,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      vote_open: selectVoteOpenOption
    });
  }, [selectSortOption, selectVoteOpenOption])

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const sortOrder = parseSortOrder()

    getOpinionPosts({
      page: pagination.next_page,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      vote_open: selectVoteOpenOption
    });
  };

  const handleSelectSession = (event: SelectChangeEvent) => {
    setSelectVoteOpenOption(Boolean(event.target.value));
  };

  const handleSort = (event: SelectChangeEvent) => {
    setSelectSortOption(event.target.value);
  };

  const createSuggestion = () => {
    navigate("/suggestion");
  };

  const parseSortOrder = () => {
    const [ type, direction ] = selectSortOption.split("_");
    const sortType = type || "created"
    const orderType = direction || "desc"

    return {
      sortType: sortType as SortType,
      order: orderType as OrderType,
    }
  }

  return (
    <div style={styles.communityContainer}>
      <LoadingModal visible={isLoading} />

      <div style={styles.communityBox}>
        <div style={styles.createSuggestionBox}>
          <Button
            onClick={() => createSuggestion()}
            style={styles.createSuggestionBtn}
            size={"large"}
            variant="contained"
            endIcon={<FontAwesomeIcon icon={faChevronRight} />}
          >
            <span style={styles.createPostBtnText}>
              {T("facilitator_list_create_suggestion")}
            </span>
          </Button>
        </div>

        <p style={styles.facilitatorDescrip}>
          {T("facilitator_list_description")}
        </p>

        <DropDown
          title={T("sort")}
          value={selectSortOption}
          options={sortOptions}
          onChange={handleSort}
        />

        <DropDown
          title={T("vote_in_session")}
          value={selectVoteOpenOption ?? ""}
          options={voteOpenOptions}
          onChange={handleSelectSession}
        />
      </div>

      {posts.map((post: any, index: number) => (
        <PostCard
          post={post}
          key={index}
        />
      ))}

      <LoadMoreButton show={pagination?.next_page > 0} onClick={loadMore} />

      <MebukuIdUpgradeBanner />

      <ErrorMessageModal
        open={showErrorModal}
        messages={errMsg}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
}
